import React, { useEffect, useRef, useState } from 'react';
import { toast } from '../../../../../../../ui/toast';
import { NotebookIcons } from '../../../../../../../icons/icons';
import { Dialog, NameEmailAvatar } from '../../../../../../../ui';
import { ICandidate } from '../../../../../../../utils/types/JobOpenings';
import { DIALOG_TYPE } from '../../../../../Constants/DialogTypes';

interface IRecordInterviewDialog {
  open: boolean;
  handleCloseRecordDialog: () => void;
  onRecordInterview?: (row: ICandidate, audioBlob: Blob) => void;
  onOpenPanelistNotes?: (row: ICandidate) => void;
  candidate: ICandidate;
}

export const RecordInterviewDialog = (props: IRecordInterviewDialog) => {
  const {
    open,
    handleCloseRecordDialog,
    onRecordInterview,
    onOpenPanelistNotes,
    candidate,
  } = props;
  const [isRecording, setIsRecording] = useState(false);
  const [isPaused, setIsPaused] = useState(false);
  const [audioBlob, setAudioBlob] = useState<Blob | null>(null);
  const [confirmSave, setConfirmSave] = useState(false);
  const [recordingTime, setRecordingTime] = useState(0);
  const [hasStartedOnce, setHasStartedOnce] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const mediaRecorder = useRef<MediaRecorder | null>(null);
  const audioChunks = useRef<Blob[]>([]);
  const timerInterval = useRef<NodeJS.Timeout>();

  useEffect(() => {
    return () => {
      if (timerInterval.current) {
        clearInterval(timerInterval.current);
      }
    };
  }, []);

  const formatTime = (seconds: number): string => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins.toString().padStart(2, '0')}:${secs
      .toString()
      .padStart(2, '0')}`;
  };

  const handleSaveRecording = async () => {
    if (!onRecordInterview || !audioBlob || !confirmSave || isSaving) return;

    setIsSaving(true);
    const duration = recordingTime;
    const toastId = toast.info(
      `Saving recording (${formatTime(duration)}). This might take a moment...`,
      {
        position: 'bottom-center',
        autoClose: false,
        closeButton: false,
      },
    );

    try {
      await onRecordInterview(candidate, audioBlob);
      toast.update(toastId, {
        render: 'Recording saved successfully!',
        type: toast.TYPE.SUCCESS,
        autoClose: 3000,
      });
      handleClose();
    } catch (error) {
      toast.update(toastId, {
        render: 'Failed to save recording. Please try again.',
        type: toast.TYPE.ERROR,
        autoClose: 5000,
      });
      setIsSaving(false);
    }
  };

  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      mediaRecorder.current = new MediaRecorder(stream);
      audioChunks.current = [];

      mediaRecorder.current.ondataavailable = (event) => {
        audioChunks.current.push(event.data);
      };

      mediaRecorder.current.onstop = () => {
        const audioBlob = new Blob(audioChunks.current, { type: 'audio/wav' });
        setAudioBlob(audioBlob);
        if (timerInterval.current) {
          clearInterval(timerInterval.current);
        }
      };

      mediaRecorder.current.start(1000);
      setIsRecording(true);
      setHasStartedOnce(true);
      setIsPaused(false);

      timerInterval.current = setInterval(() => {
        setRecordingTime((prev) => prev + 1);
      }, 1000);
    } catch (error) {
      console.error('Error accessing microphone:', error);
    }
  };

  const pauseRecording = () => {
    if (mediaRecorder.current && isRecording) {
      mediaRecorder.current.pause();
      setIsPaused(true);
      if (timerInterval.current) {
        clearInterval(timerInterval.current);
      }
    }
  };

  const resumeRecording = () => {
    if (mediaRecorder.current && isRecording) {
      mediaRecorder.current.resume();
      setIsPaused(false);
      timerInterval.current = setInterval(() => {
        setRecordingTime((prev) => prev + 1);
      }, 1000);
    }
  };

  const stopRecording = () => {
    if (mediaRecorder.current && isRecording) {
      mediaRecorder.current.stop();
      setIsRecording(false);
      setIsPaused(false);
      mediaRecorder.current.stream.getTracks().forEach((track) => track.stop());
      if (timerInterval.current) {
        clearInterval(timerInterval.current);
      }
    }
  };

  const handleClose = () => {
    if (isRecording) {
      stopRecording();
    }
    setAudioBlob(null);
    setConfirmSave(false);
    setRecordingTime(0);
    setHasStartedOnce(false);
    setIsSaving(false);
    handleCloseRecordDialog();
  };

  return (
    <Dialog
        isOpen={open}
        title='Record Interview'
        handleApply={handleSaveRecording}
        cancelButtonLable='Cancel'
        confirmButtonLable={isSaving ? 'Saving...' : 'Save Recording'}
        disabled={!audioBlob || !confirmSave || isSaving}
        dialogType={DIALOG_TYPE.ERROR}
        onClose={handleClose}
    >
      <div className='flex flex-col gap-4 p-4'>
        <span className='text-md text-primary-textGray'>Candidate</span>
        <div className='flex items-center gap-3 mb-6'>
          <NameEmailAvatar
              avatar=''
              email={candidate.email || 'Unknown Email'}
              name={`${candidate.firstName || 'Unknown'} ${
                candidate.lastName || 'Candidate'
              }`}
          />
        </div>
        <div className='flex flex-col items-center gap-4'>
          <div className='text-xl font-semibold'>
            { formatTime(recordingTime) }
          </div>

          <div className='flex justify-center gap-4'>
            { !isRecording ? (
              <button
                  className='p-3 rounded-full bg-blue-500 hover:bg-blue-600 text-white flex items-center justify-center'
                  onClick={startRecording}
                  title='Start Recording'
              >
                <svg
                    className='w-6 h-6'
                    viewBox='0 0 24 24'
                    fill='currentColor'
                >
                  <path d='M12 14c1.66 0 3-1.34 3-3V5c0-1.66-1.34-3-3-3S9 3.34 9 5v6c0 1.66 1.34 3 3 3z' />
                  <path d='M17 11c0 2.76-2.24 5-5 5s-5-2.24-5-5H5c0 3.53 2.61 6.43 6 6.92V21h2v-3.08c3.39-.49 6-3.39 6-6.92h-2z' />
                </svg>
              </button>
            ) : (
              <>
                { !isPaused ? (
                  <button
                      className='p-3 rounded-full bg-yellow-500 hover:bg-yellow-600 text-white flex items-center justify-center'
                      onClick={pauseRecording}
                      title='Pause Recording'
                  >
                    <svg
                        className='w-6 h-6'
                        viewBox='0 0 24 24'
                        fill='currentColor'
                    >
                      <rect x='6' y='4' width='4' height='16' />
                      <rect x='14' y='4' width='4' height='16' />
                    </svg>
                  </button>
                ) : (
                  <button
                      className='p-3 rounded-full bg-green-500 hover:bg-green-600 text-white flex items-center justify-center'
                      onClick={resumeRecording}
                      title='Resume Recording'
                  >
                    <svg
                        className='w-6 h-6'
                        viewBox='0 0 24 24'
                        fill='currentColor'
                    >
                      <path d='M8 5v14l11-7z' />
                    </svg>
                  </button>
                ) }
                <button
                    className='p-3 rounded-full bg-red-500 hover:bg-red-600 text-white flex items-center justify-center'
                    onClick={stopRecording}
                    title='Stop Recording'
                >
                  <svg
                      className='w-6 h-6'
                      viewBox='0 0 24 24'
                      fill='currentColor'
                  >
                    <rect x='6' y='6' width='12' height='12' />
                  </svg>
                </button>
              </>
            ) }
            <button
                className='p-3 rounded-full bg-emerald-500 hover:bg-emerald-600 text-white flex items-center justify-center'
                onClick={() => onOpenPanelistNotes?.(props.candidate)}
                title='Open Panelist Notes'
                disabled={isSaving}
            >
              <NotebookIcons />
            </button>
          </div>
        </div>

        { hasStartedOnce && !isRecording && (
          <div className='flex flex-col gap-4 mt-4'>
            { audioBlob ? (
              <>
                <audio
                    controls
                    src={URL.createObjectURL(audioBlob)}
                    className='w-full'
                />
                <div className='flex items-start gap-2 p-4 bg-yellow-50 border border-yellow-200 rounded'>
                  <svg
                      className='w-5 h-5 text-yellow-500 flex-shrink-0 mt-1'
                      viewBox='0 0 24 24'
                      fill='currentColor'
                  >
                    <path d='M1 21h22L12 2 1 21zm12-3h-2v-2h2v2zm0-4h-2v-4h2v4z' />
                  </svg>
                  <div className='flex flex-col gap-2'>
                    <p className='text-sm text-yellow-700'>
                      Please review your recording before saving. Once saved,
                      you will not be able to modify it.
                      { recordingTime > 300 && (
                        <span className='block mt-1 font-medium'>
                          Note: This is a { Math.round(recordingTime / 60) }{ ' ' }
                          minute recording and may take a few moments to save.
                        </span>
                      ) }
                    </p>
                    <label className='flex items-center gap-2'>
                      <input
                          type='checkbox'
                          checked={confirmSave}
                          onChange={(e) => setConfirmSave(e.target.checked)}
                          className='form-checkbox h-5 w-5 text-blue-600 rounded'
                          disabled={isSaving}
                      />
                      <span className='text-sm font-medium'>
                        I confirm that I have reviewed the recording and it is
                        ready to be saved
                      </span>
                    </label>
                  </div>
                </div>
              </>
            ) : (
              <div className='text-center text-gray-500'>
                Recording completed. Click the microphone icon to start a new
                recording.
              </div>
            ) }
          </div>
        ) }
      </div>
    </Dialog>
  );
};
